import React from 'react'
import { BottomDrawer } from '@/shared/ui/BottomDrawer'
import type { OpportunityItem } from '../types'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { useResultsEnabled, useResultsDrawer } from '@/modules/resultados'
import { useCommunityEnabled, useCommunityDrawer } from '@/modules/comunidade'

type ClosedActionSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: OpportunityItem | null
}

export default function ClosedOpportunityActionSheet({
  open,
  onOpenChange,
  item,
}: ClosedActionSheetProps) {
  const { openModal, openDebug } = useOportunidadeModal()
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const resultsEnabled = useResultsEnabled()
  const { openResultsDrawer } = useResultsDrawer()
  const communityEnabled = useCommunityEnabled()
  const { openCommunityDrawer } = useCommunityDrawer()

  const canUseResults = resultsEnabled === true && !!item
  const canUseChat = communityEnabled === true && !!item
  const hasNumericId = !!item && Number.isFinite(Number(item.id))

  const header = (
    <div className="flex items-center gap-2">
      {item?.logo_url ? (
        <img
          src={item.logo_url || ''}
          alt={item?.name || item?.symbol || ''}
          className="h-6 w-6 rounded-full object-cover"
        />
      ) : (
        <div className="h-6 w-6 rounded-full bg-muted" />
      )}
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">
          {item?.name || item?.symbol || ''}
        </div>
        <div className="truncate text-xs text-foreground/70">{item?.symbol || ''}</div>
      </div>
    </div>
  )

  const withClose = (action: () => void) => () => {
    onOpenChange(false)
    setTimeout(action, 180)
  }

  return (
    <BottomDrawer open={open} onOpenChange={onOpenChange} header={header}>
      <div className="space-y-2">
        {/* Detalhes */}
        <button
          type="button"
          className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
          onClick={withClose(() => {
            if (!item) return
            openModal({
              id: item.id,
              symbol: item.symbol,
              name: item.name,
              logo_url: item.logo_url,
              kind: 'closed',
            })
          })}
        >
          Detalhes
        </button>

        {/* Resultado */}
        {item && canUseResults && hasNumericId && (
          <button
            type="button"
            className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={withClose(() => {
              if (!item) return
              openResultsDrawer({
                opportunityId: Number(item.id),
                symbol: item.symbol,
                name: item.name,
                logoUrl: item.logo_url,
              })
            })}
          >
            Resultado
          </button>
        )}

        {/* Bate papo */}
        {item && canUseChat && hasNumericId && (
          <button
            type="button"
            className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={withClose(() => {
              if (!item) return
              openCommunityDrawer({
                opportunityId: Number(item.id),
                symbol: item.symbol,
                name: item.name,
                logoUrl: item.logo_url,
              })
            })}
          >
            Bate papo
          </button>
        )}

        {/* Debug (somente master) */}
        {item && isMaster && (
          <button
            type="button"
            className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={withClose(() => {
              openDebug({
                id: item.id,
                symbol: item.symbol,
                name: item.name,
                logo_url: item.logo_url,
                kind: 'closed',
              })
            })}
          >
            Debug
          </button>
        )}
      </div>
    </BottomDrawer>
  )
}
